import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControlLabel,
  Paper,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useVendorAccessGate from "../../../hooks/useVendorAccessGate";
import { useAuth } from "../../../services/providers/authProvider";
import { ErrorOutline } from "@mui/icons-material";
import ConfirmationModal from "../../Shared/confirmationModal";
import { httpClient } from "../../../services/httpClient";
import api from "../../../config/gatewayConfig";

// Helper component for single recharge amount selection
function RicaricaSection({ amount, handlePresetAmount, handleAmountChange, errorAmount }) {
  return (
    <Box sx={{ mb: 4, textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Rubik, sans-serif", color: "#0b3d87", mb: 3 }}>
        Seleziona l'importo da ricaricare*
      </Typography>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="center" gap={2}>
        {[50, 100, 200].map((value) => (
          <Button
            key={value}
            variant={amount === value.toString() ? "contained" : "outlined"}
            onClick={() => handlePresetAmount(value)}
            sx={{
              borderRadius: "20px",
              fontFamily: "Rubik, sans-serif",
              fontWeight: "bold",
              px: 3,
              py: 1,
              backgroundColor: amount === value.toString() ? "#0b3d87" : "transparent",
              color: amount === value.toString() ? "white" : "#0b3d87",
              borderColor: "#0b3d87",
              "&:hover": {
                backgroundColor: amount === value.toString() ? "#0b3d87df" : "#0b3d8715",
                borderColor: "#0b3d87",
              }
            }}
          >
            {value}€
          </Button>
        ))}
        <Typography sx={{ color: "#0b3d87", mx: 2, fontWeight: "bold", fontFamily: "Rubik, sans-serif" }}>oppure</Typography>
        <TextField
          type="number"
          placeholder="Inserisci importo personalizzato"
          slotProps={{ htmlInput: { min: 10, max: 10000 } }}
          size="small"
          sx={{
            width: '250px',
            backgroundColor: "#f0f5ff",
            '& .MuiOutlinedInput-root': {
              borderRadius: "20px",
              '& fieldset': {
                borderColor: '#0b3d87',
              },
              '&:hover fieldset': {
                borderColor: '#0b3d87',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0b3d87',
              },
            },
            '& .MuiInputBase-input': {
              color: '#0b3d87',
              fontStyle: 'italic',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#0b3d87',
              opacity: 1,
              fontStyle: 'italic',
            },
            '& .MuiFormHelperText-root': {
              color: '#0b3d87',
            }
          }}
          variant="outlined"
          value={amount}
          onChange={handleAmountChange}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Box>
    </Box>
  );
}

// Helper component for rendering billing details table
function DetailsTable({ amount }) {
  const chosenAmountLabel = amount ? `${amount}€` : "-";
  const creditObtainedLabel = amount ? `${amount}€` : "-";
  const totalToPayLabel = amount ? `${amount}€` : "-";

  const rows = [
    { label: "IMPORTO SCELTO", value: chosenAmountLabel },
    { label: "CREDITO OTTENUTO", value: creditObtainedLabel },
    { label: "COMMISSIONI", value: "-" },
    { label: "TOTALE DA PAGARE", value: totalToPayLabel }
  ];

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: { xs: '100%', md: '60%' },
        borderRadius: 2,
        boxShadow: 'none',
        mt: 2,
        mx: 'auto',
      }}
    >
      <Table>
        <TableBody>
          {rows.map((item, index) => (
            <TableRow
              key={item.label}
              sx={{
                backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
              }}
            >
              <TableCell
                sx={{
                  color: "#0b3d87",
                  fontWeight: "bold",
                  fontFamily: "Rubik, sans-serif",
                  border: "none",
                  py: 2,
                  textAlign: "left"
                }}
              >
                {item.label}
              </TableCell>
              <TableCell
                sx={{
                  color: "#0b3d87",
                  fontFamily: "Rubik, sans-serif",
                  border: "none",
                  py: 2,
                  textAlign: "right"
                }}
              >
                {item.value}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const Ricarica = () => {
  useVendorAccessGate();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorAmount, setErrorAmount] = useState("");
  const { vendorId } = useAuth() || {};
  const safeVendorId = Number(vendorId);
  const [loading, setLoading] = useState(false);
  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [modalCancelOpen, setModalCancelOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancel") === "true") {
      setModalCancelOpen(true);
      window.history.replaceState({}, document.title, "/ricarica");
    }
  }, []);

  const handlePresetAmount = (value) => {
    setAmount(value.toString());
    setErrorAmount("");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.trim();
    if (value === "") {
      setAmount("");
      setErrorAmount("");
      return;
    }
    const numericValue = Number.parseFloat(value);
    if (Number.isNaN(numericValue) || numericValue < 10 || numericValue > 10000) {
      setErrorAmount("L'importo deve essere compreso tra 10€ e 10.000€.");
    } else {
      setErrorAmount("");
    }
    setAmount(value);
  };

  const handlePaymentNavigation = async () => {
    if (!termsAccepted) return;
    if (!amount || errorAmount) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("vendorId", safeVendorId);
      params.append("amount", amount);

      const response = await httpClient.post(
        `${api.apiBaseUrl}/biling/customer/create-checkout-session`,
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const checkoutUrl = response.data;
      if (checkoutUrl) {
        globalThis.location.href = checkoutUrl;
      } else {
        throw new Error("URL di checkout non valido restituito dal server.");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error?.response?.data?.message || "Si è verificato un errore durante la creazione della sessione di pagamento.");
      setModalErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
        <Box display="flex" alignItems="center" mb={2} onClick={() => navigate("/gestioneBudget")} sx={{ cursor: "pointer", width: "fit-content" }}>
          <Typography variant="body1" sx={{ fontWeight: "bold", fontFamily: "Rubik, sans-serif", color: "#0b3d87", ml: 1 }}>⬅ Riepilogo Movimenti</Typography>
        </Box>
        <Typography variant="h4" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87", textAlign: "center" }}>Effettua una Ricarica Singola</Typography>

        <Paper sx={{ p: { xs: 3, md: 6 }, mt: 3, borderRadius: "12px", backgroundColor: "#fff", boxShadow: "0px 4px 20px rgba(11, 61, 135, 0.05)", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="body1" sx={{ fontFamily: "Rubik, sans-serif", color: "#4f5d73", mb: 4, textAlign: "center", maxWidth: "800px" }}>
            Carica un importo una tantum sul tuo saldo residuo per pagare i tuoi click. Le ricariche vengono aggiunte immediatamente al tuo portafoglio.
          </Typography>

          <RicaricaSection
            amount={amount}
            handlePresetAmount={handlePresetAmount}
            handleAmountChange={handleAmountChange}
            errorAmount={errorAmount}
          />

          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#9a9a9a", mb: 2, mt: 5, textAlign: "center" }}>Dettagli di Ricarica</Typography>
          
          <DetailsTable amount={amount} />

          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  color: '#0b3d87',
                  '&.Mui-checked': {
                    color: '#0b3d87',
                  },
                }}
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
            }
            label={
              <Typography component="span" sx={{ color: '#0b3d87', fontFamily: "Rubik, sans-serif" }}>
                <a href="https://wowcheprezzi.it/termini-condizioni" target="_blank" rel="noopener noreferrer" style={{ color: "#0b3d87", textDecoration: 'none', fontFamily: "Rubik, sans-serif" }}>
                  Accetta i Termini e Condizioni
                </a>
              </Typography>
            }
            sx={{ mt: 3 }}
          />

          <Box display="flex" justifyContent="center" mt={4} gap={3} width="100%">
            <Button
              variant="outlined"
              onClick={() => navigate("/gestioneBudget")}
              sx={{
                fontFamily: "Rubik, sans-serif",
                backgroundColor: "#f5f5f5",
                borderRadius: "20px",
                color: '#0b3d87',
                border: 'none',
                px: 4,
                '&:hover': {
                  backgroundColor: '#0b3d8722',
                  borderColor: '#0b3d87',
                }
              }}
            >
              Annulla
            </Button>

            <Button
              onClick={handlePaymentNavigation}
              variant="contained"
              disabled={!termsAccepted || !amount || !!errorAmount || loading}
              sx={{
                borderRadius: "20px",
                backgroundColor: "#0b3d87",
                color: "white",
                px: 5,
                fontFamily: "Rubik, sans-serif",
                fontWeight: "bold",
                '&:hover': {
                  backgroundColor: '#092b6a',
                }
              }}
            >
              {loading ? "Elaborazione..." : "Procedi al Pagamento"}
            </Button>
          </Box>
        </Paper>
      </Box>

        <ConfirmationModal
          open={modalErrorOpen}
          onClose={() => setModalErrorOpen(false)}
          title="Errore di Pagamento"
          message={errorMessage || "Si è verificato un errore durante il pagamento. Riprova più tardi."}
          subtitle=""
          icon={<ErrorOutline sx={{ color: "white", fontSize: 50 }} />}
          confirmText="OK"
          hideCancelButton={true}
          onConfirm={() => setModalErrorOpen(false)}
        />
        <ConfirmationModal
          open={modalCancelOpen}
          onClose={() => setModalCancelOpen(false)}
          title="Pagamento Annullato"
          message="Hai annullato la procedura di ricarica. Nessun importo ti è stato addebitato."
          subtitle=""
          icon={<ErrorOutline sx={{ color: "white", fontSize: 50 }} />}
          confirmText="Chiudi"
          hideCancelButton={true}
          onConfirm={() => setModalCancelOpen(false)}
        />
    </>
  );
};

export default Ricarica;
